// A multipart/form-data body built by hand, byte for byte what a browser sends for a FormData. It keeps the
// upload on the typed client (bearer token, silent refresh) in every runtime, including the test runner,
// whose DOM FormData the native fetch does not recognise.

const enc = new TextEncoder();

/** The HTML spec's escaping of names in Content-Disposition: `"` → %22, CR → %0D, LF → %0A. */
function quote(v: string): string {
  return v.replace(/"/g, '%22').replace(/\r/g, '%0D').replace(/\n/g, '%0A');
}

/** The file's bytes: Blob.arrayBuffer where it exists, FileReader otherwise (older DOMs). */
function readBytes(file: Blob): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer().then((b) => new Uint8Array(b));
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsArrayBuffer(file);
  });
}

export async function multipart(fields: Record<string, string | File>): Promise<{ body: Uint8Array; contentType: string }> {
  const boundary = `----aiss${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  const parts: Uint8Array[] = [];
  for (const [name, value] of Object.entries(fields)) {
    if (typeof value === 'string') {
      parts.push(enc.encode(`--${boundary}\r\nContent-Disposition: form-data; name="${quote(name)}"\r\n\r\n${value}\r\n`));
    } else {
      const head = `--${boundary}\r\nContent-Disposition: form-data; name="${quote(name)}"; filename="${quote(value.name)}"\r\nContent-Type: ${value.type || 'application/octet-stream'}\r\n\r\n`;
      parts.push(enc.encode(head), await readBytes(value), enc.encode('\r\n'));
    }
  }
  parts.push(enc.encode(`--${boundary}--\r\n`));
  const body = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    body.set(p, at);
    at += p.length;
  }
  return { body, contentType: `multipart/form-data; boundary=${boundary}` };
}
